/**
 * The life of ONE keyed cost command (a confirmation or a reconcile apply), shared by both screens
 * (batch 8 T11, #3032). See `pendingOperation.ts` for the contract of the stored intent.
 *
 * What this adds on top of the storage contract:
 *
 *  - The intent belongs to the signed-in session (`CostingScope`). When the session, tenant or user
 *    changes, this state is replaced by the new session's own (its stored intent, or none) in the same
 *    render, and nothing of the previous one stays reachable: not the request, not the outcome, not a
 *    retry.
 *  - Everything that is exposed is hidden the moment the permission to confirm is gone, while a request
 *    that is already in flight is left to finish on its own.
 *  - Nothing is sent by a lifecycle effect, a token rotation or an unlock. A request leaves only from
 *    `begin` and `retry`, and only after the current session, generation and permission were checked
 *    again at that moment (`takeFence`).
 *  - Every late result, failure and `finally` is checked against the fence it started under BEFORE it may
 *    change state, delete the stored intent or announce anything. A result that arrives into another
 *    session or without the permission changes nothing; the request is simply left unresolved ("unknown"),
 *    which is true: the stored intent is resent as the same key and payload when its owner asks.
 *  - An in-flight request is shared by key, so a second click or a remounted screen joins it instead of
 *    sending again.
 */
import { useCallback, useRef, useState } from 'react';
import {
  COSTING_CONFIRM_PERMISSION,
  fenceHolds,
  isOwnerChanged,
  scopeId,
  takeFence,
  useCostingAccess,
  type CostingScope,
} from './costingSession';
import { isUncertainFailure, type Loaded, type PersistenceStatus, type SaveStatus } from './pendingOperation';

export type FlowPhase = 'idle' | 'sending' | 'unknown';

export interface IntentBase {
  readonly key: string;
  readonly scope: CostingScope;
}

/** How one kind of command is stored and sent. */
export interface IntentAdapter<I extends IntentBase, R> {
  readonly kind: string;
  /** The permission the command needs; checked before dispatch and after every late answer. */
  readonly permission?: string;
  load(scope: CostingScope | null): Loaded<I>;
  save(intent: I): SaveStatus;
  clear(intent: I): void;
  dropUnusable(scope: CostingScope): void;
  /** The request itself. Called at most once per key at a time (a second caller joins the first). */
  send(intent: I): Promise<R>;
}

interface State<I, R> {
  /** Whose state this is (`null`: nobody signed in). */
  readonly sid: string | null;
  readonly intent: I | null;
  readonly phase: FlowPhase;
  readonly persistence: PersistenceStatus | null;
  readonly restored: boolean;
  readonly discarded: boolean;
  readonly unreadable: boolean;
  readonly refusal: unknown;
  readonly outcome: R | null;
}

function initial<I extends IntentBase, R>(adapter: IntentAdapter<I, R>, scope: CostingScope | null): State<I, R> {
  const loaded = adapter.load(scope);
  return {
    sid: scope === null ? null : scopeId(scope),
    intent: loaded.pending,
    phase: loaded.pending === null ? 'idle' : 'unknown',
    persistence: loaded.pending === null ? null : 'stored',
    restored: loaded.pending !== null,
    discarded: loaded.discarded,
    unreadable: loaded.unreadable,
    refusal: null,
    outcome: null,
  };
}

/** Requests in flight in this page, by kind, session and key: one request per key, whoever asks. */
const inflight = new Map<string, Promise<unknown>>();

export interface KeyedIntent<I extends IntentBase, R> {
  readonly intent: I | null;
  readonly phase: FlowPhase;
  readonly persistence: PersistenceStatus | null;
  readonly restored: boolean;
  /** A stored record that could not be used (unscoped old format, damaged): reported, never resent. */
  readonly discarded: boolean;
  /** The browser would not let the stored requests be read, so one cannot be ruled out. */
  readonly storageUnreadable: boolean;
  readonly refusal: unknown;
  readonly outcome: R | null;
  /** Writes a new intent down and sends it; refuses while another is unresolved. */
  begin(build: (scope: CostingScope) => I): Promise<R | null>;
  /** Sends the stored intent again: the same key, the same payload. */
  retry(): Promise<R | null>;
  discard(): void;
  dismissRefusal(): void;
  dismissOutcome(): void;
  dismissDiscarded(): void;
}

export function useKeyedIntent<I extends IntentBase, R>(adapter: IntentAdapter<I, R>): KeyedIntent<I, R> {
  const access = useCostingAccess();
  const permission = adapter.permission ?? COSTING_CONFIRM_PERMISSION;
  const adapterRef = useRef(adapter);
  adapterRef.current = adapter;
  const sid = access.scope === null ? null : scopeId(access.scope);

  const [stored, setStored] = useState<State<I, R>>(() => initial(adapter, access.scope));
  let state = stored;
  if (stored.sid !== sid) {
    // Another session, tenant or user: its own state replaces this one in this very render.
    state = initial(adapter, access.scope);
    setStored(state);
  }

  // Nothing is exposed unless this session is active and may confirm: a downgrade hides the request, its
  // costs and its outcome in the same render, while anything in flight is left alone.
  const visible = access.active && (permission === COSTING_CONFIRM_PERMISSION ? access.canConfirm : access.canRead);

  const busy = useRef<{ sid: string | null } | null>(null);
  const patch = useCallback((forSid: string, change: (previous: State<I, R>) => State<I, R>) => {
    setStored((previous) => (previous.sid === forSid ? change(previous) : previous));
  }, []);

  const run = useCallback(
    async (intent: I): Promise<R | null> => {
      const own = adapterRef.current;
      const forSid = scopeId(intent.scope);
      if (busy.current !== null && busy.current.sid === forSid) return null;
      // Credentials, session and permission as they are NOW, not as they were when the button was drawn.
      const fence = takeFence(intent.scope, permission);
      if (fence === null) return null;
      const token = { sid: forSid };
      busy.current = token;
      patch(forSid, (previous) => ({ ...previous, phase: 'sending', refusal: null }));
      const flightId = `${own.kind}|${forSid}|${intent.key}`;
      try {
        let flight = inflight.get(flightId) as Promise<R> | undefined;
        if (flight === undefined) {
          flight = own.send(intent);
          inflight.set(flightId, flight);
          const started = flight;
          const release = () => {
            if (inflight.get(flightId) === started) inflight.delete(flightId);
          };
          started.then(release, release);
        }
        const result = await flight;
        if (!fenceHolds(fence)) {
          patch(forSid, (previous) => ({ ...previous, phase: 'unknown' }));
          return null;
        }
        own.clear(intent);
        patch(forSid, (previous) =>
          previous.intent?.key === intent.key ? { ...previous, intent: null, persistence: null, restored: false, phase: 'idle', outcome: result } : previous,
        );
        return result;
      } catch (error) {
        if (isOwnerChanged(error) || !fenceHolds(fence)) {
          // Not sent, or answered into a session that is no longer this one: nothing may change.
          patch(forSid, (previous) => ({ ...previous, phase: 'unknown' }));
          return null;
        }
        if (isUncertainFailure(error)) {
          // Keep the key and the payload: the request may have committed.
          patch(forSid, (previous) => ({ ...previous, phase: 'unknown' }));
        } else {
          // The server read it and wrote nothing: free to correct and send as a new request.
          own.clear(intent);
          patch(forSid, (previous) =>
            previous.intent?.key === intent.key
              ? { ...previous, intent: null, persistence: null, restored: false, phase: 'idle', refusal: error }
              : previous,
          );
        }
        return null;
      } finally {
        if (busy.current === token) busy.current = null;
      }
    },
    [patch, permission],
  );

  const begin = useCallback(
    (build: (scope: CostingScope) => I): Promise<R | null> => {
      const scope = access.scope;
      if (!visible || scope === null || state.intent !== null) return Promise.resolve(null);
      const forSid = scopeId(scope);
      if (busy.current !== null && busy.current.sid === forSid) return Promise.resolve(null);
      if (takeFence(scope, permission) === null) return Promise.resolve(null);
      const own = adapterRef.current;
      const intent = build(scope);
      // Written down BEFORE it is sent.
      const status = own.save(intent);
      if (status === 'conflict') {
        // An unresolved intent of this session exists that this screen had not loaded: it wins, nothing new is sent.
        patch(forSid, () => initial(own, scope));
        return Promise.resolve(null);
      }
      patch(forSid, (previous) => ({ ...previous, intent, persistence: status, restored: false, refusal: null, outcome: null }));
      return run(intent);
    },
    [access.scope, permission, patch, run, state.intent, visible],
  );

  const retry = useCallback((): Promise<R | null> => {
    if (!visible || state.intent === null) return Promise.resolve(null);
    return run(state.intent); // the same key and the same payload
  }, [run, state.intent, visible]);

  const discard = useCallback(() => {
    const intent = state.intent;
    if (!visible || intent === null || takeFence(intent.scope, permission) === null) return;
    adapterRef.current.clear(intent);
    patch(scopeId(intent.scope), (previous) => ({ ...previous, intent: null, persistence: null, restored: false, phase: 'idle' }));
  }, [patch, permission, state.intent, visible]);

  const dismissRefusal = useCallback(() => {
    if (sid !== null) patch(sid, (previous) => ({ ...previous, refusal: null }));
  }, [patch, sid]);
  const dismissOutcome = useCallback(() => {
    if (sid !== null) patch(sid, (previous) => ({ ...previous, outcome: null }));
  }, [patch, sid]);
  const dismissDiscarded = useCallback(() => {
    if (access.scope === null || sid === null) return;
    adapterRef.current.dropUnusable(access.scope);
    patch(sid, (previous) => ({ ...previous, discarded: false }));
  }, [access.scope, patch, sid]);

  return {
    intent: visible ? state.intent : null,
    phase: visible ? state.phase : 'idle',
    persistence: visible ? state.persistence : null,
    restored: visible && state.restored,
    discarded: visible && state.discarded,
    storageUnreadable: visible && state.unreadable,
    refusal: visible ? state.refusal : null,
    outcome: visible ? state.outcome : null,
    begin,
    retry,
    discard,
    dismissRefusal,
    dismissOutcome,
    dismissDiscarded,
  };
}
